import type {
  AuditEntry,
  AuditModule,
  AuditOutcome,
  AuditSeverity,
  UserRole,
} from '@micarahr/shared-types';
import { insert, paginate, readAll, type Page, type PageRequest } from './collections';
import { runtime, serviceCall } from './runtime';
import { nextId } from './ids';
import { env } from '../config/env';
import { apiAuditService } from './registries.api';

/**
 * The audit log.
 *
 * Append-only by construction: this module exports `record` and two readers,
 * and nothing else. There is no update and no delete — not because they are
 * hidden, but because they do not exist. An audit log an administrator can edit
 * is not an audit log.
 *
 * `record` never throws. A mutation that succeeded must not appear to fail
 * because its audit write did; the failure is surfaced through the log's own
 * integrity counter instead.
 */

export interface AuditActor {
  userId: string;
  name: string;
  roles: UserRole[];
}

export interface RecordAuditInput {
  actor: AuditActor;
  module: AuditModule;
  action: string;
  summary: string;
  recordType: string;
  recordId: string;
  recordLabel: string;
  subjectEmployeeId?: string | null;
  outcome?: AuditOutcome;
  severity?: AuditSeverity;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  metadata?: Record<string, unknown>;
}

let failedWrites = 0;

export async function recordAudit(input: RecordAuditInput): Promise<void> {
  const entry: AuditEntry = {
    id: nextId('aud'),
    occurredAt: runtime.now(),
    actorId: input.actor.userId,
    actorName: input.actor.name,
    actorRoles: input.actor.roles,
    module: input.module,
    action: input.action,
    summary: input.summary,
    recordType: input.recordType,
    recordId: input.recordId,
    recordLabel: input.recordLabel,
    subjectEmployeeId: input.subjectEmployeeId ?? null,
    outcome: input.outcome ?? 'succeeded',
    severity: input.severity ?? 'info',
    before: input.before ?? null,
    after: input.after ?? null,
    metadata: input.metadata ?? {},
    // Populated by the backend once it exists. Shown as "not captured in this
    // phase" rather than invented, because a fabricated IP in an audit log is
    // worse than an absent one.
    requestId: null,
    ipAddress: null,
    userAgent: typeof navigator === 'undefined' ? null : navigator.userAgent,
  };

  try {
    await insert<AuditEntry>('auditEntries', entry);
  } catch {
    failedWrites += 1;
  }
}

export interface AuditFilter extends PageRequest {
  module?: AuditModule | 'all';
  actorId?: string;
  action?: string;
  employeeId?: string;
  severity?: AuditSeverity | 'all';
  outcome?: AuditOutcome | 'all';
  from?: string;
  to?: string;
}

const mockAuditService = {
  async list(filter: AuditFilter): Promise<Page<AuditEntry>> {
    return serviceCall('auditService', () => {
      let rows = readAll<AuditEntry>('auditEntries');

      if (filter.module && filter.module !== 'all') {
        rows = rows.filter((row) => row.module === filter.module);
      }
      if (filter.actorId) rows = rows.filter((row) => row.actorId === filter.actorId);
      if (filter.action) rows = rows.filter((row) => row.action === filter.action);
      if (filter.employeeId) {
        rows = rows.filter((row) => row.subjectEmployeeId === filter.employeeId);
      }
      if (filter.severity && filter.severity !== 'all') {
        rows = rows.filter((row) => row.severity === filter.severity);
      }
      if (filter.outcome && filter.outcome !== 'all') {
        rows = rows.filter((row) => row.outcome === filter.outcome);
      }
      if (filter.from) rows = rows.filter((row) => row.occurredAt >= filter.from!);
      if (filter.to) rows = rows.filter((row) => row.occurredAt <= `${filter.to!}T23:59:59.999Z`);

      rows.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));

      return paginate(
        rows as unknown as Record<string, unknown>[],
        { ...filter, sortBy: filter.sortBy },
        (row) => `${row.summary} ${row.actorName} ${row.action} ${row.recordLabel}`,
      ) as unknown as Page<AuditEntry>;
    });
  },

  /** The modules the log actually contains, for the Module filter. */
  async modules(): Promise<string[]> {
    return serviceCall('auditService', () =>
      [...new Set(readAll<AuditEntry>('auditEntries').map((entry) => entry.module))].sort(),
    );
  },

  async get(id: string): Promise<AuditEntry | null> {
    return serviceCall('auditService', () => {
      return readAll<AuditEntry>('auditEntries').find((entry) => entry.id === id) ?? null;
    });
  },

  /** Entries for one record, oldest first — the history strip on a detail page. */
  async forRecord(recordId: string): Promise<AuditEntry[]> {
    return serviceCall('auditService', () =>
      readAll<AuditEntry>('auditEntries')
        .filter((entry) => entry.recordId === recordId)
        .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt)),
    );
  },

  /** Recent activity for the dashboard, already filtered to human-meaningful actions. */
  async recentActivity(limit = 12): Promise<AuditEntry[]> {
    return serviceCall('auditService', () =>
      readAll<AuditEntry>('auditEntries')
        // Sign-ins would drown everything else and tell an HR manager nothing
        // about the state of their people (§10).
        .filter((entry) => entry.module !== 'auth' && entry.module !== 'simulation')
        .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))
        .slice(0, limit),
    );
  },

  /** Number of audit writes that failed this session. Surfaced in the Simulation Centre. */
  failedWriteCount(): number {
    return failedWrites;
  },
};

/**
 * Audit adapter selector.
 *
 * Read paths only. The API has no update or delete for audit events, and this
 * adapter offers none either — immutability that depends on a UI having no
 * delete button is not immutability.
 */
export const auditService =
  env().dataSource === 'api' ? { ...mockAuditService, ...apiAuditService } : mockAuditService;
